import type { Quaternion } from 'three';
import { Vector3 } from 'three';
import { quaternionFromBasis, rightFromForwardUp } from '../utils/math';

export type SurfaceId = 'floor' | 'ceiling' | 'left' | 'right' | 'front' | 'back';

export const ALL_SURFACE_IDS: SurfaceId[] = ['floor', 'ceiling', 'left', 'right', 'front', 'back'];

/**
 * A walkable surface.
 *
 * Every surface owns a local frame:
 *   normal  -> local up
 *   forward -> local forward (the surface's canonical tangent)
 *   right   -> local right, derived as `forward x up` so the frame stays
 *              right-handed with three.js' local +Z = back convention.
 */
export interface SurfaceHole {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
}

/**
 * A piece of a face that carries metal, in local u/v.
 *
 * A face is one rectangle in the collision and walk logic, but where another
 * zone shares part of the plane that part belongs to the neighbour: drawing
 * both would z-fight, and treating the shared part as exterior hull would let a
 * shot through an interior partition breach the ship. Segments are the pieces
 * this face actually owns, so each piece of a wall is drawn exactly once and
 * `hullAt` can answer per point.
 */
export interface SurfaceSegment {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
  /** True when this piece is exterior hull: breachable by the AR. */
  hull: boolean;
}

/** The result of clipping a step at the rim of an opening, in local u/v. */
export interface HoleClamp {
  u: number;
  v: number;
  /** Unit local direction pointing into the opening across that rim. */
  intoU: number;
  intoV: number;
}

export class Surface {
  readonly normal: Vector3;
  readonly forward: Vector3;
  readonly right: Vector3;
  readonly basis: Quaternion;
  readonly edges: SurfaceEdge[] = [];
  /** Doorways and other openings in this surface, in local u/v. */
  readonly holes: SurfaceHole[] = [];
  /** The pieces of this face that are actually this surface's metal. */
  readonly segments: SurfaceSegment[] = [];

  constructor(
    readonly id: SurfaceId,
    normal: Vector3,
    forward: Vector3,
    readonly origin: Vector3,
    readonly uMin: number,
    readonly uMax: number,
    readonly vMin: number,
    readonly vMax: number,
    readonly magnetic: boolean,
    /** Globally unique key, `zone:face` for multi-zone levels, `face` otherwise. */
    readonly key: string = id,
    /** The zone this surface belongs to. */
    readonly zoneId: string = 'root',
    /** True for an exterior hull face: breachable by the AR. */
    readonly hull: boolean = false,
  ) {
    this.normal = normal.clone().normalize();
    this.forward = forward.clone().normalize();
    this.right = rightFromForwardUp(this.forward, this.normal, new Vector3());
    this.basis = quaternionFromBasis(this.right, this.normal, this.forward.clone().negate());
    this.segments.push({ uMin, uMax, vMin, vMax, hull });
  }

  /**
   * Replace the pieces this face owns.
   *
   * The room calls this when a neighbouring zone shares part of the plane: the
   * shared part belongs to the neighbour, so it is removed from this face.
   */
  setSegments(segments: readonly SurfaceSegment[]): void {
    this.segments.length = 0;
    for (const segment of segments) this.segments.push(segment);
  }

  /** True when a local point is exterior hull, so a shot there breaches. */
  hullAt(u: number, v: number): boolean {
    for (const segment of this.segments) {
      if (!segment.hull) continue;
      if (u >= segment.uMin && u <= segment.uMax && v >= segment.vMin && v <= segment.vMax) {
        return true;
      }
    }
    return false;
  }

  /** Register an opening in the surface, used to let rays pass through doors. */
  addHole(hole: SurfaceHole): void {
    this.holes.push(hole);
  }

  /** True when a local point lies inside one of the surface's openings. */
  containsHole(u: number, v: number, margin = 0): boolean {
    for (const hole of this.holes) {
      if (
        u >= hole.uMin - margin &&
        u <= hole.uMax + margin &&
        v >= hole.vMin - margin &&
        v <= hole.vMax + margin
      ) {
        return true;
      }
    }
    return false;
  }

  /** True when the surface has no material under a local point. */
  isSolidAt(u: number, v: number, margin = 0): boolean {
    return this.contains(u, v) && !this.containsHole(u, v, margin);
  }

  /**
   * Stop a step from one local point to another at the edge of a hole.
   *
   * An opening has no metal, so the magnet cannot hold a body whose support
   * point crosses into it. The segment is clipped against the near side of any
   * hole it enters, `margin` short of the rim, and the point just outside the
   * opening is returned, together with `intoU`/`intoV`: the unit local
   * direction that points into the opening across that rim. A caller can use
   * it to cancel only the motion into the hole, so a diagonal step slides
   * along the edge instead of stopping dead. Null means the step is clear.
   */
  clampOutOfHoles(
    fromU: number,
    fromV: number,
    toU: number,
    toV: number,
    margin = 0,
    out: HoleClamp = { u: 0, v: 0, intoU: 0, intoV: 0 },
  ): HoleClamp | null {
    if (!this.containsHole(toU, toV, margin)) return null;
    // A body already inside an opening has nothing to clip against: the caller
    // has to decide (a body over a doorway is not supported at all).
    if (this.containsHole(fromU, fromV)) return null;

    const du = toU - fromU;
    const dv = toV - fromV;

    let bestHole: SurfaceHole | null = null;
    let bestEnter = 1;
    let axis: 0 | 1 = 0;
    let sign = 1;

    for (const hole of this.holes) {
      const uMin = hole.uMin - margin;
      const uMax = hole.uMax + margin;
      const vMin = hole.vMin - margin;
      const vMax = hole.vMax + margin;

      // Parametric entry into the expanded rectangle. The slab that sets the
      // entry is the rim the step ran into.
      let enter = 0;
      let exit = 1;
      let hit = true;
      let entryAxis: 0 | 1 = 0;
      let entrySign = 1;
      if (Math.abs(du) < 1e-9) {
        if (fromU < uMin || fromU > uMax) hit = false;
      } else {
        const t0 = (uMin - fromU) / du;
        const t1 = (uMax - fromU) / du;
        if (Math.min(t0, t1) > enter) {
          enter = Math.min(t0, t1);
          entryAxis = 0;
          entrySign = du > 0 ? 1 : -1;
        }
        exit = Math.min(exit, Math.max(t0, t1));
      }
      if (Math.abs(dv) < 1e-9) {
        if (fromV < vMin || fromV > vMax) hit = false;
      } else {
        const t0 = (vMin - fromV) / dv;
        const t1 = (vMax - fromV) / dv;
        if (Math.min(t0, t1) > enter) {
          enter = Math.min(t0, t1);
          entryAxis = 1;
          entrySign = dv > 0 ? 1 : -1;
        }
        exit = Math.min(exit, Math.max(t0, t1));
      }
      if (!hit || enter > exit) continue;

      if (enter === 0) {
        // The body is already resting against the rim, so no slab set the
        // entry: the nearest side is the one it is pressed against.
        const toMinU = fromU - uMin;
        const toMaxU = uMax - fromU;
        const toMinV = fromV - vMin;
        const toMaxV = vMax - fromV;
        entryAxis = 0;
        entrySign = 1;
        let nearest = toMinU;
        if (toMaxU < nearest) {
          nearest = toMaxU;
          entrySign = -1;
        }
        if (toMinV < nearest) {
          nearest = toMinV;
          entryAxis = 1;
          entrySign = 1;
        }
        if (toMaxV < nearest) {
          entryAxis = 1;
          entrySign = -1;
        }
      }

      if (bestHole && enter >= bestEnter) continue;
      bestHole = hole;
      bestEnter = enter;
      axis = entryAxis;
      sign = entrySign;
    }

    if (!bestHole) return null;

    // Stop at the rim along the axis the step ran into, and keep the rest of
    // the step: a diagonal move slides along the edge instead of stopping dead.
    out.intoU = axis === 0 ? sign : 0;
    out.intoV = axis === 1 ? sign : 0;
    if (axis === 0) {
      out.u = sign > 0 ? bestHole.uMin - margin : bestHole.uMax + margin;
      out.v = toV;
    } else {
      out.v = sign > 0 ? bestHole.vMin - margin : bestHole.vMax + margin;
      out.u = toU;
    }
    return out;
  }

  /** Local coordinates (u along right, v along forward) of a world point. */
  toLocal(point: Vector3, out = { u: 0, v: 0 }): { u: number; v: number } {
    const dx = point.x - this.origin.x;
    const dy = point.y - this.origin.y;
    const dz = point.z - this.origin.z;
    out.u = dx * this.right.x + dy * this.right.y + dz * this.right.z;
    out.v = dx * this.forward.x + dy * this.forward.y + dz * this.forward.z;
    return out;
  }

  /** World position of local coordinates at height `h` above the surface. */
  toWorld(u: number, v: number, h: number, out = new Vector3()): Vector3 {
    out.copy(this.origin);
    out.addScaledVector(this.right, u);
    out.addScaledVector(this.forward, v);
    out.addScaledVector(this.normal, h);
    return out;
  }

  /** Signed distance from the surface plane; positive means above the surface. */
  signedDistance(point: Vector3): number {
    return (
      (point.x - this.origin.x) * this.normal.x +
      (point.y - this.origin.y) * this.normal.y +
      (point.z - this.origin.z) * this.normal.z
    );
  }

  contains(u: number, v: number, margin = 0): boolean {
    return (
      u >= this.uMin - margin &&
      u <= this.uMax + margin &&
      v >= this.vMin - margin &&
      v <= this.vMax + margin
    );
  }

  /**
   * Heading of a world-space direction expressed in this surface's frame
   * (radians, + = right).
   *
   * `out` is provided so the hot path never allocates: see
   * `docs/ARCHITECTURE.md` §12.
   */
  headingOf(direction: Vector3, out = new Vector3()): number {
    out.copy(direction);
    out.addScaledVector(this.normal, -out.dot(this.normal));
    if (out.lengthSq() < 1e-8) return 0;
    out.normalize();
    return Math.atan2(out.dot(this.right), out.dot(this.forward));
  }
}

export interface SurfaceEdge {
  /** The surface on the other side of this edge, as a global surface key. */
  neighbor: string;
  /** Which local axis the edge line lies on. */
  axis: 'u' | 'v';
  /** The coordinate of the edge line on that axis. */
  value: number;
  /** Extent of the edge along the other axis. */
  alongMin: number;
  alongMax: number;
  /** +1 when the neighbour lies at greater coordinates on `axis`. */
  sign: 1 | -1;
  /** A normal face seam, or a doorway into another zone. Defaults to `face`. */
  kind?: 'face' | 'portal';
  /** On a portal, the target axis that carries the along coordinate. */
  targetAxis?: 'u' | 'v';
  /** On a portal, the constant coordinate on the target surface. */
  targetValue?: number;
  /** On a portal, the target coordinate range that maps to `alongMin…alongMax`. */
  targetAlongMin?: number;
  targetAlongMax?: number;
}

/** Face order for portal-safe tie-breaking in `findCrossedEdge`. */
export function faceNameOf(key: string): string {
  const colon = key.indexOf(':');
  return colon >= 0 ? key.slice(colon + 1) : key;
}
